import React from 'react';
import { FiPhone, FiSearch, FiCalendar, FiArrowRight } from 'react-icons/fi';
import useIsMobile from '../utils/useIsMobile';

const steps = [
  {
    number: '01',
    icon: FiPhone,
    title: 'Call Us',
    description: 'Reach out to our team. We\'re here to answer your questions and guide you to the right plan.',
    color: '#3b82f6',
    bg: '#eff6ff',
  },
  {
    number: '02',
    icon: FiSearch,
    title: 'Choose Our Service',
    description: 'Browse our Internet, Cable TV, Wi-Fi, and Wireless plans and pick the one that best fits your home or business needs.',
    color: '#8b5cf6',
    bg: '#f5f3ff',
  },
  {
    number: '03',
    icon: FiCalendar,
    title: 'Schedule Installation',
    description: 'Pick a convenient date and time. We will help you with the installation setup.',
    color: '#10b981',
    bg: '#ecfdf5',
  },
];

const Journey = () => {
  const isMobile = useIsMobile(900);

  return (
    <section style={{ padding: isMobile ? 'clamp(40px, 8vw, 60px) var(--cc-px)' : 'clamp(64px, 7vw, 100px) var(--cc-px)', background: '#f8f9fc', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <div className="cc-container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 56 }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 12 }}>How It Works</span>
          <h2 style={{ fontSize: 'clamp(1.7rem, 3.5vw, 4.5rem)', fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
            Getting Started is Easy
          </h2>
        </div>

        {isMobile ? (
          /* ── Mobile: vertical timeline ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative', maxWidth: 480, margin: '0 auto' }}>
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isLast = i === steps.length - 1;
              return (
                <div key={i} style={{ display: 'flex', gap: 0 }}>
                  {/* Left timeline track */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 52, flexShrink: 0 }}>
                    <div style={{
                      width: 46, height: 46, borderRadius: '50%',
                      background: step.bg, border: `2.5px solid ${step.color}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, zIndex: 1,
                      boxShadow: `0 4px 14px ${step.color}30`,
                    }}>
                      <Icon style={{ width: 18, height: 18, color: step.color }} />
                    </div>
                    {!isLast && (
                      <div style={{
                        width: 2, flex: 1, minHeight: 24,
                        background: `linear-gradient(to bottom, ${step.color}80, ${steps[i + 1].color}50)`,
                        margin: '4px 0',
                      }} />
                    )}
                  </div>

                  {/* Card content */}
                  <div style={{
                    flex: 1, marginLeft: 16,
                    background: '#fff', borderRadius: 18, padding: '20px 18px',
                    border: '1.5px solid #e8edf5',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
                    marginBottom: isLast ? 0 : 16,
                    position: 'relative', overflow: 'hidden',
                  }}>
                    <span style={{
                      position: 'absolute', top: 10, right: 14,
                      fontSize: '3.2rem', fontWeight: 900, color: step.color,
                      opacity: 0.06, lineHeight: 1, userSelect: 'none',
                      letterSpacing: '-0.04em',
                    }}>{step.number}</span>

                    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: step.color, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                      Step {step.number}
                    </span>
                    <h3 style={{ fontWeight: 800, color: '#0f172a', fontSize: '1rem', margin: '0 0 8px', letterSpacing: '-0.01em' }}>
                      {step.title}
                    </h3>
                    <p style={{ color: '#64748b', fontSize: '0.87rem', lineHeight: 1.72, margin: 0 }}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Desktop: horizontal cards ── */
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, flexWrap: 'wrap', justifyContent: 'center' }}>
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isLast = i === steps.length - 1;
              return (
                <React.Fragment key={i}>
                  <div style={{
                    flex: '1 1 300px', maxWidth: 400,
                    background: '#fff', borderRadius: 22, padding: '36px 30px',
                    border: '1.5px solid #e8edf5',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
                    position: 'relative', overflow: 'hidden',
                    transition: 'transform 0.22s, box-shadow 0.22s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = `0 14px 40px ${step.color}1a`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.04)'; }}
                  >
                    <span style={{
                      position: 'absolute', top: 16, right: 20,
                      fontSize: '4.5rem', fontWeight: 900, color: step.color,
                      opacity: 0.06, lineHeight: 1, userSelect: 'none',
                      letterSpacing: '-0.04em',
                    }}>{step.number}</span>

                    <div style={{ width: 54, height: 54, borderRadius: 15, background: step.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                      <Icon style={{ width: 22, height: 22, color: step.color }} />
                    </div>

                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: step.color, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: 10 }}>
                      Step {step.number}
                    </span>

                    <h3 style={{ fontWeight: 800, color: '#0f172a', fontSize: '1.1rem', margin: '0 0 12px', letterSpacing: '-0.01em' }}>
                      {step.title}
                    </h3>
                    <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.72, margin: 0 }}>
                      {step.description}
                    </p>
                  </div>

                  {!isLast && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px', flexShrink: 0, alignSelf: 'center' }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#f1f5f9', border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FiArrowRight style={{ width: 16, height: 16, color: '#94a3b8' }} />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default Journey;
